import React, { useState, useEffect } from "react";
import Input from "../../components/UI/Input/Input";
import Checkbox from "../../components/Checkbox/Checkbox";
import SelectedDepartmentsChips from "./SelectedDepartmentsChips";
import ModalActions from "./ModalActions";
import styles from "./EmployeeList.module.scss";
import { AddEmployee, GetDepartments, GetEmployeeList } from "../../ApiCalls";

const EmployeeDuplicateForm = ({ employee, onCancel, setEmployees }) => {
  const [defaultData, setDefaultData] = useState({
    name: "",
    code: "",
    department: Array.isArray(employee.department)
      ? employee.department
      : employee.department.split(",").map((d) => d.trim()),
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [departments, setDepartments] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await GetDepartments();
        if (response.data?.success) {
          setDepartments(response.data.departmentList);
        }
      } catch (error) {
        console.error("Failed to load departments:", error);
      }
    };
    fetchDepartments();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!defaultData.name) newErrors.name = "Required!";
    if (!defaultData.code) newErrors.code = "Required!";
    if (defaultData.department.length === 0) newErrors.department = "Required!";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddEmployee = async () => {
    if (!validateForm()) return;

    try {
      const response = await AddEmployee({
        name: defaultData.name,
        code: defaultData.code,
        department: defaultData.department.join(","),
      });

      if (response.data?.success) {
        const employeesResponse = await GetEmployeeList();
        if (employeesResponse.data?.success) {
          setEmployees(employeesResponse.data.employeeList);
        }
        onCancel();
      }
    } catch (error) {
      console.error("Failed to add employee:", error);
    }
  };

  const getInputProps = (field) => ({
    config: {
      type: "text",
      value: defaultData[field],
      placeholder: field.charAt(0).toUpperCase() + field.slice(1),
      onChange: (event) => {
        setDefaultData((previousState) => ({
          ...previousState,
          [field]: event.target.value,
        }));
        setErrors((previousErrors) => ({
          ...previousErrors,
          [field]: undefined,
        }));
      },
    },
    label: field.charAt(0).toUpperCase() + field.slice(1),
    error: errors[field],
  });

  const filteredDepartments = departments.filter((department) =>
    department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleDepartment = (department) => {
    setDefaultData((previousState) => ({
      ...previousState,
      department: previousState.department.includes(department)
        ? previousState.department.filter(
            (currentDepartment) => currentDepartment !== department
          )
        : [...previousState.department, department],
    }));
    setErrors((previousErrors) => ({
      ...previousErrors,
      department: undefined,
    }));
  };

  return (
    <div className={styles.modalContent}>
      <div className={styles.inputWrapper}>
        <Input inputProperties={getInputProps("name")} />
      </div>

      <div className={styles.inputWrapper}>
        <Input inputProperties={getInputProps("code")} />
      </div>

      <div className={styles.departmentSelection}>
        <label className={styles.departmentLabel}>Departments</label>
        <input
          type="text"
          placeholder="Search departments..."
          className={styles.searchInput}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
        <div className={styles.departmentsList}>
          {filteredDepartments.length > 0 ? (
            filteredDepartments.map((department) => (
              <div
                key={department}
                className={styles.departmentItem}
                onClick={() => toggleDepartment(department)}
              >
                <Checkbox
                  checked={defaultData.department.includes(department)}
                  onChange={() => toggleDepartment(department)}
                />
                <span className={styles.departmentName}>{department}</span>
              </div>
            ))
          ) : (
            <div className={styles.noResults}>No departments found</div>
          )}
        </div>
        {errors.department && (
          <p className={styles.errorText}>{errors.department}</p>
        )}
      </div>
      <SelectedDepartmentsChips
        selectedDepartments={defaultData.department}
        onRemove={(department) => {
          setDefaultData((previousState) => ({
            ...previousState,
            department: previousState.department.filter(
              (currentDepartment) => currentDepartment !== department
            ),
          }));
        }}
      />

      <ModalActions
        onCancel={onCancel}
        onAction={handleAddEmployee}
        actionLabel="Add Duplicate"
      />
    </div>
  );
};

export default EmployeeDuplicateForm;
